import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Twitter, MessageCircle, Copy, Share2, Check, Send } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useWallet } from "@/contexts/WalletContext";

interface ShareButtonsProps {
  coinId?: string;
  coinName?: string;
  coinTicker?: string;
  title?: string;
  url?: string;
  compact?: boolean;
}

export function ShareButtons({ 
  coinId, 
  coinName, 
  coinTicker, 
  title,
  url,
  compact = false 
}: ShareButtonsProps) {
  const { toast } = useToast();
  const { publicKey } = useWallet();
  const [copied, setCopied] = useState(false);

  const shareUrl = url || (coinId 
    ? `${window.location.origin}/coin/${coinId}` 
    : window.location.href);
  
  const shareText = title || (coinName && coinTicker
    ? `Check out $${coinTicker} (${coinName}) on TheMoonRun! 🚀🌙`
    : "Check out TheMoonRun - the best Solana meme coin launchpad! 🚀🌙");

  const trackShare = async (platform: string) => {
    if (!publicKey) return;
    
    try {
      await supabase.from("social_shares").insert({
        user_wallet: publicKey,
        coin_id: coinId || null,
        platform,
        share_url: shareUrl,
      });
    } catch (error) {
      console.error("Failed to track share:", error);
    }
  };

  const shareToTwitter = () => {
    trackShare("twitter");
    const twitterUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
    window.open(twitterUrl, "_blank", "width=600,height=400");
  };

  const shareToTelegram = () => {
    trackShare("telegram");
    const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`;
    window.open(telegramUrl, "_blank", "width=600,height=400");
  };

  const shareToDiscord = () => {
    trackShare("discord");
    // Discord doesn't have a direct share URL, so copy a formatted message
    const discordText = `${shareText}\n${shareUrl}`;
    navigator.clipboard.writeText(discordText);
    toast({ title: "Copied for Discord!", description: "Paste in your Discord channel" });
  };

  const copyLink = async () => {
    trackShare("copy");
    await navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast({ title: "Link Copied!", description: "Share it with your friends" });
    setTimeout(() => setCopied(false), 2000);
  };

  const nativeShare = async () => {
    if (navigator.share) {
      trackShare("native");
      try {
        await navigator.share({
          title: coinName || "TheMoonRun",
          text: shareText,
          url: shareUrl,
        });
      } catch (error) {
        // User cancelled or error
      }
    } else {
      copyLink();
    }
  };

  if (compact) {
    return (
      <div className="flex items-center gap-1">
        <Button size="sm" variant="ghost" onClick={shareToTwitter} className="h-8 w-8 p-0" aria-label="Share to Twitter">
          <Twitter className="w-4 h-4" />
        </Button>
        <Button size="sm" variant="ghost" onClick={shareToTelegram} className="h-8 w-8 p-0" aria-label="Share to Telegram">
          <Send className="w-4 h-4" />
        </Button>
        <Button size="sm" variant="ghost" onClick={copyLink} className="h-8 w-8 p-0" aria-label={copied ? "Link copied" : "Copy link"}>
          {copied ? <Check className="w-4 h-4 text-success" /> : <Copy className="w-4 h-4" />}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button 
        size="sm" 
        variant="outline" 
        onClick={shareToTwitter}
        className="gap-2"
      >
        <Twitter className="w-4 h-4" />
        Twitter
      </Button>
      <Button 
        size="sm" 
        variant="outline" 
        onClick={shareToTelegram}
        className="gap-2"
      >
        <Send className="w-4 h-4" />
        Telegram
      </Button>
      <Button 
        size="sm" 
        variant="outline" 
        onClick={shareToDiscord}
        className="gap-2"
      >
        <MessageCircle className="w-4 h-4" />
        Discord
      </Button>
      <Button 
        size="sm" 
        variant="outline" 
        onClick={copyLink}
        className="gap-2"
      >
        {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
        {copied ? "Copied!" : "Copy Link"}
      </Button>
      {navigator.share && (
        <Button 
          size="sm" 
          variant="outline" 
          onClick={nativeShare}
          className="gap-2"
        >
          <Share2 className="w-4 h-4" />
          Share
        </Button>
      )}
    </div>
  );
}
